import type { PreferenceStoreIdentifier } from '@transcend-io/privacy-types';

import type { ConsentPreferenceUpdateInput } from '@main/cm-types';
import type { EnrichedIdentifiers, PurposeInput } from '@main/sombra-types';

import type { Purpose } from '../CommunicationsPreferences/components';

/**
 * Create input for updateDataSubjectConsentPreferences mutation
 *
 * @param coreIdentifier - the core identifier of the data subject
 * @param identifiers - set of identifiers on consent record
 * @param partition - the partition to update
 * @param purposes - the selected purposes/preferences
 * @returns input for updateDataSubjectConsentPreferences mutation
 */
export function getUpdateDataSubjectConsentPreferencesInput(
  coreIdentifier: string,
  identifiers: PreferenceStoreIdentifier[],
  partition: string,
  purposes: Record<string, Purpose>,
): {
  /** Update Consent Preferences input */
  input: ConsentPreferenceUpdateInput;
  /** Diffie Hellman input */
  dhEncryptedInput: {
    /** Identifiers to encrypt */
    identifiersToEncrypt: PreferenceStoreIdentifier[][];
    /** Attested auth context for triggers dsr's  */
    attestedExtraIdentifiers: Record<string, unknown>;
  };
} {
  const identifiersToEncrypt =
    identifiers.length > 0
      ? [identifiers]
      : [[{ name: 'email', value: coreIdentifier }]];
  return {
    input: {
      partition,
      purposes: Object.entries(purposes).map(
        ([purpose, { consent, preferences }]) => ({
          purpose,
          consent,
          ...(preferences
            ? {
                preferences: Object.entries(preferences).map(
                  ([topic, choice]) => {
                    // remove __typename from choice before calling graphql
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
                    const { __typename, ...rest } = choice as any;
                    return { topic, choice: { ...rest } };
                  },
                ),
              }
            : {}),
        }),
      ) as PurposeInput[],
    },
    dhEncryptedInput: {
      identifiersToEncrypt,
      // TODO: https://transcend.height.app/T-44099 - move this grouping/dedupe logic to consent-backend-utils
      attestedExtraIdentifiers: identifiers.reduce((acc, { value, name }) => {
        acc[name] ||= [];
        if (value !== coreIdentifier) {
          acc[name].push({ value });
        }
        return acc;
      }, {} as EnrichedIdentifiers),
    },
  };
}
